import type { Map as MlMap, GeoJSONSource } from "maplibre-gl";

/**
 * v0.9.5c — national-scale (z4–6) river names: ONE label per river, placed at run time.
 * Thai names are longer on screen than the rivers at that scale, so they're point labels; the
 * candidates (river-label-far.json, from preprocess/build_river_labels.mjs) lie along each river's
 * long chains. Higher layers — dam % / C.13, over-bank staffs, POPNIX gauges, the 📍 — always win
 * placement and change with the data, so each name takes the first candidate whose box is clear of
 * what is rendered NOW — on the river, else just beside it (most-constrained river first, then the
 * biggest; a still-clear previous pick is kept → no flicker).
 * MapLibre's own collision stays on as the final word (allow-overlap false).
 */
type Far = { name: string; dis: number; pts: [number, number][] };
type Box = [number, number, number, number]; // x0, y0, x1, y1 (px)

const SRC = "river-labels-far-src";
export const FAR_LAYER = "river-labels-far";
const BLOCKERS = ["route-names", "user-pin-block", "tw-over", "tw-near", "tw-labels", "dams-layer", "dams-arrow", "dams-y54-label", "bkk-gauges-label", "dr-user-label", "storm-cur-icon"];
const FONT_PX = 12;

let names: Far[] = [];
type Anchor = "center" | "left" | "right" | "top" | "bottom";
const ANCHORS: Anchor[] = ["center", "left", "right", "top", "bottom"];
const picked = new Map<string, [number, Anchor]>(); // name → [candidate index, anchor]
let timer: number | null = null;
let ctx: CanvasRenderingContext2D | null = null;

export async function installFarLabels(map: MlMap, beforeId: string | undefined, paint: Record<string, unknown>): Promise<void> {
  map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addLayer({
    id: FAR_LAYER, type: "symbol", source: SRC, maxzoom: 7,
    layout: {
      "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": FONT_PX,
      // on the river, or just beside it when the river itself is crowded (chosen per label below)
      "text-anchor": ["get", "anchor"], "text-offset": ["match", ["get", "anchor"], "left", ["literal", [0.5, 0]], "right", ["literal", [-0.5, 0]],
        "top", ["literal", [0, 0.4]], "bottom", ["literal", [0, -0.4]], ["literal", [0, 0]]],
      "symbol-sort-key": ["-", 0, ["get", "dis"]], "text-allow-overlap": false, "text-padding": 4,
    },
    paint: paint as never,
  }, beforeId);
  try {
    const r = await fetch("/data/river-label-far.json");
    if (!r.ok) return;
    names = ((await r.json()) as { names: Far[] }).names;
  } catch { return; }
  // a new view / new blockers → forget the spots MapLibre refused last time and start the rounds again
  const schedule = (): void => { if (timer !== null) clearTimeout(timer); timer = window.setTimeout(() => { timer = null; rejected.clear(); rounds = 0; place(map); }, 300); };
  map.on("moveend", schedule);
  // blockers arrive / change with their data (ThaiWater, dams, gauges, the pin)
  // (only their sources — the ambient flow animation rewrites its own source every frame)
  map.on("data", (e) => {
    const ev = e as { dataType?: string; sourceId?: string };
    if (ev.dataType !== "source" || !ev.sourceId) return;
    if (BLOCKERS.some((l) => (map.getLayer(l) as { source?: string } | undefined)?.source === ev.sourceId)) schedule();
  });
  schedule();
}

function textWidth(s: string): number {
  if (!ctx) { ctx = document.createElement("canvas").getContext("2d"); if (ctx) ctx.font = `${FONT_PX}px "Noto Sans Thai", "Noto Sans", sans-serif`; }
  return ctx ? ctx.measureText(s).width : s.length * FONT_PX * 0.6;
}
// which feature property names the icon image, per blocker layer (others: their fixed image)
const ICON_PROP: Record<string, string> = { "dams-layer": "bucket", "dams-arrow": "arr", "tw-over": "icon", "tw-near": "icon" };
const FIXED_ICON: Record<string, string> = { "user-pin-block": "user-pin-block" };

/** Evaluate a number / ["interpolate", ["linear"], ["zoom"], z0, v0, …] icon-size at zoom z. */
function sizeAt(v: unknown, z: number): number {
  if (typeof v === "number") return v;
  if (!Array.isArray(v) || v[0] !== "interpolate") return 1;
  const stops = v.slice(3) as number[];
  if (z <= stops[0]!) return stops[1]!;
  for (let i = 2; i < stops.length; i += 2) {
    const z0 = stops[i - 2]!, v0 = stops[i - 1]!, z1 = stops[i]!, v1 = stops[i + 1]!;
    if (z <= z1) return v0 + ((z - z0) / (z1 - z0)) * (v1 - v0);
  }
  return stops[stops.length - 1]!;
}

/** Screen boxes a rendered blocker occupies: its bottom-anchored icon (real image size × the
 *  layer's icon-size now) and, for a dam, the % / release text under it. */
function blockerBoxes(map: MlMap, layer: string, props: Record<string, unknown>, x: number, y: number): Box[] {
  const id = FIXED_ICON[layer] ?? (ICON_PROP[layer] ? String(props[ICON_PROP[layer]!] ?? "") : "");
  const im = id ? (map as unknown as { style: { getImage(i: string): { data: { width: number; height: number }; pixelRatio: number } | undefined } }).style.getImage(id) : undefined;
  const k = sizeAt(map.getLayoutProperty(layer, "icon-size"), map.getZoom());
  const iw = im ? (im.data.width / im.pixelRatio) * k : 24, ih = im ? (im.data.height / im.pixelRatio) * k : 28;
  // v0.9.10b: the icon's real box — its anchor (bottom / bottom-left) and icon-offset (× icon-size), e.g. the
  // C.13 / พระราม 6 arrow sits 9 px right / 20 px up of its point; a box at the bare point missed it
  const anc = String(map.getLayoutProperty(layer, "icon-anchor") ?? "bottom");
  const off = map.getLayoutProperty(layer, "icon-offset"), ox = Array.isArray(off) ? Number(off[0]) * k : 0, oy = Array.isArray(off) ? Number(off[1]) * k : 0;
  const x0 = anc.endsWith("left") ? x + ox : anc.endsWith("right") ? x + ox - iw : x + ox - iw / 2;
  const out: Box[] = [[x0 - 2, y + oy - ih - 2, x0 + iw + 2, y + oy + 2]];
  // (dam callouts on → the pins' own text is off the map: only the icon blocks)
  if (layer === "dams-layer" && typeof props.pct === "string" && props.pct && map.getLayoutProperty("dams-layer", "text-field") !== "") {
    // v0.9.10a: two lines under the icon — short name (0.85 em) over the value (13 px) — + "ปี 54" when on
    const name = String(props.name ?? "") + (props.stale ? " (เก่า)" : "");
    const tw = Math.max(textWidth(props.pct) * 13 / FONT_PX, textWidth(name) * 11 / FONT_PX);
    const y54 = map.getLayer("dams-y54-label") && map.getLayoutProperty("dams-y54-label", "visibility") !== "none";
    out.push([x - tw / 2 - 3, y, x + tw / 2 + 3, y + 36 + (y54 ? 15 : 0)]);
  }
  return out;
}
const hit = (a: Box, b: Box): boolean => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

function place(map: MlMap): void {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src || !names.length || map.getZoom() >= 7) { if (placedBoxes.length) { placedBoxes = []; window.dispatchEvent(new Event("fs-far-labels")); } return; }
  const W = map.getCanvas().clientWidth, H = map.getCanvas().clientHeight;
  // every rendered point symbol above us → a box around its anchor (icon above, text below)
  const layers = BLOCKERS.filter((l) => map.getLayer(l));
  const taken: Box[] = [];
  for (const f of map.queryRenderedFeatures({ layers })) {
    if (f.geometry.type !== "Point") continue;
    const p = map.project(f.geometry.coordinates as [number, number]);
    taken.push(...blockerBoxes(map, f.layer.id, f.properties ?? {}, p.x, p.y));
  }
  // what covers the map on screen (sheet, buttons, chip, bars) — a name under it would be unseen
  const cv = map.getCanvas().getBoundingClientRect();
  for (const q of [".sheet", "#locate-fab", "#summary-fab", "#flow-fab", "#sos-fab", "#storm-sim-btn", "#river-chip", ".search-box", ".pr-days", ".rf-chips", ".menu-btn", ".maplibregl-ctrl-attrib", ".storm-pill", ".top-fab", ".tr-stack"]) {
    for (const el of document.querySelectorAll<HTMLElement>(q)) {
      if (el.hidden || getComputedStyle(el).display === "none") continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) taken.push([r.left - cv.left, r.top - cv.top, r.right - cv.left, r.bottom - cv.top]);
    }
  }
  const features: GeoJSON.Feature[] = [];
  const blockers = taken.length;
  const plans = names.map((n) => {
    // MapLibre's collision box for this text incl. text-padding, measured on the live map:
    // (text width + 5) × 22 px; +1 px a side for safety. Its centre moves with the anchor + text-offset.
    const w = (textWidth(n.name) + 5) / 2 + 1, h = 12, off = FONT_PX * 0.5, offV = FONT_PX * 0.4 + 7.2;
    // anchor = the side of the TEXT that sits on the point ("left" → text to the right of it, "top" → below it)
    const boxAt = (i: number, a: Anchor): Box | null => {
      const p = map.project(n.pts[i]!);
      const cx = a === "left" ? p.x + off + w : a === "right" ? p.x - off - w : p.x;
      const cy = a === "top" ? p.y + offV : a === "bottom" ? p.y - offV : p.y;
      const b: Box = [cx - w, cy - h, cx + w, cy + h];
      return b[0] < 0 || b[2] > W || b[1] < 0 || b[3] > H ? null : b; // off screen
    };
    const tries: { t: [number, Anchor]; b: Box }[] = [];
    const prev = picked.get(n.name);
    const order: [number, Anchor][] = [];
    for (const a of ANCHORS) for (const i of n.pts.keys()) order.push([i, a]);
    if (prev) order.unshift(prev);
    for (const t of order) {
      if (rejected.has(`${n.name}|${t[0]}|${t[1]}`)) continue; // MapLibre hid the name there this view
      const b = boxAt(t[0], t[1]); if (b && !taken.slice(0, blockers).some((k) => hit(k, b))) tries.push({ t, b });
    }
    return { n, tries };
  });
  // most-constrained first: a river with few free spots (e.g. ท่าจีน squeezed beside เจ้าพระยา) picks
  // before one that has room elsewhere; then bigger rivers first
  plans.sort((x, y) => (x.tries.length === 0 ? 1 : 0) - (y.tries.length === 0 ? 1 : 0) || Math.min(x.tries.length, 8) - Math.min(y.tries.length, 8) || y.n.dis - x.n.dis);
  for (const { n, tries } of plans) {
    const c = tries.find((x) => !taken.some((k) => hit(k, x.b)));
    if (!c) { picked.delete(n.name); continue; } // nowhere clear on screen → no label (MapLibre would hide it anyway)
    picked.set(n.name, c.t);
    taken.push(c.b);
    features.push({ type: "Feature", properties: { name: n.name, dis: n.dis, anchor: c.t[1] }, geometry: { type: "Point", coordinates: n.pts[c.t[0]]! } });
  }
  src.setData({ type: "FeatureCollection", features });
  // v0.9.10b: our boxes are estimates — check what MapLibre really drew once it has placed the new names
  // (the map may never go "idle": the flow animation redraws every frame) and re-pick the hidden ones
  if (verifyTimer !== null) clearTimeout(verifyTimer);
  verifyTimer = window.setTimeout(() => { verifyTimer = null; verify(map); }, 600);
  // v0.9.10a: the dam callouts are laid out AROUND these names (screen boxes, viewport px)
  placedBoxes = taken.slice(blockers).map((b) => [b[0] + cv.left, b[1] + cv.top, b[2] + cv.left, b[3] + cv.top] as Box);
  window.dispatchEvent(new Event("fs-far-labels"));
}
let placedBoxes: Box[] = [];
const rejected = new Set<string>(); // "<name>|<candidate>|<anchor>" MapLibre hid in this view
let rounds = 0, verifyTimer: number | null = null;
/** names we placed but MapLibre's own collision hid → that spot is refused, the name tries its next one */
function verify(map: MlMap): void {
  if (map.getZoom() >= 7 || !map.getLayer(FAR_LAYER)) return;
  const shown = new Set(map.queryRenderedFeatures({ layers: [FAR_LAYER] }).map((f) => String(f.properties?.name)));
  let again = false;
  for (const [name, t] of picked) if (!shown.has(name)) { rejected.add(`${name}|${t[0]}|${t[1]}`); picked.delete(name); again = true; }
  if (again && rounds < 4) { rounds++; place(map); }
}
/** Screen boxes of the far river names currently placed (viewport px) — the dam callouts keep out of them. */
export function farLabelBoxes(): Box[] { return placedBoxes; }
